import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Flag, Search } from '@mui/icons-material';
import apiService from '../services/apiService';

const DriverRatingPage = () => {
  const [ratings, setRatings] = useState([]);
  const [filteredRatings, setFilteredRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRatings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getRatings();

      if (response.success) {
        // Filter only driver ratings
        const driverRatings = response.data.filter(
          rating => rating.type === 'driver' || rating.ratingType === 'driver'
        );
        setRatings(driverRatings);
        setFilteredRatings(driverRatings);
      } else {
        setError(response.message || 'Failed to load ratings');
        loadMockData();
      }
    } catch (err) {
      console.error('Ratings error:', err);
      setError('Failed to load ratings');
      loadMockData();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  // Filter ratings when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredRatings(ratings);
    } else {
      const searchLower = searchTerm.toLowerCase().trim();
      const filtered = ratings.filter(rating => {
        // Get CRN from various possible fields
        const crn = (rating.crn || rating.rideId || rating.bookingId || '').toLowerCase();
        const driverName = (rating.targetName || rating.ratedName || '').toLowerCase();
        const phoneNumber = (rating.driverPhone || rating.targetPhone || rating.mobile || '').toLowerCase();
        const reviewerName = (rating.reviewerName || rating.raterName || '').toLowerCase();
        
        return crn.includes(searchLower) || 
               driverName.includes(searchLower) || 
               phoneNumber.includes(searchLower) ||
               reviewerName.includes(searchLower);
      });
      setFilteredRatings(filtered);
    }
  }, [searchTerm, ratings]);

  const loadMockData = () => {
    // Filtered mock data - only driver ratings with phone numbers and CRN
    const mockData = [
      {
        id: '1',
        crn: 'CRN2025001',
        rideId: 'RIDE001',
        reviewerName: 'Priya Sharma',
        targetName: 'Rajesh Kumar',
        driverPhone: '9876543210',
        type: 'driver',
        rating: 5,
        review: 'Excellent service, very professional driver!',
        createdAt: '2025-01-08 11:00 AM',
      },
      {
        id: '3',
        crn: 'CRN2025003',
        rideId: 'RIDE003',
        reviewerName: 'Sneha Reddy',
        targetName: 'Vikram Singh',
        driverPhone: '8765432109',
        type: 'driver',
        rating: 2,
        review: 'Driver was rude and drove rashly.',
        createdAt: '2025-01-08 01:00 PM',
      },
      {
        id: '4',
        crn: 'CRN2025004',
        rideId: 'RIDE004',
        reviewerName: 'Amit Patel',
        targetName: 'Rahul Sharma',
        driverPhone: '7654321098',
        type: 'driver',
        rating: 4,
        review: 'Good driving, reached on time.',
        createdAt: '2025-01-08 02:00 PM',
      },
      {
        id: '5',
        crn: 'CRN2025005',
        rideId: 'RIDE005',
        reviewerName: 'Neha Gupta',
        targetName: 'Suresh Reddy',
        driverPhone: '6543210987',
        type: 'driver',
        rating: 3,
        review: 'Average driving experience.',
        createdAt: '2025-01-08 03:00 PM',
      },
    ];
    setRatings(mockData);
    setFilteredRatings(mockData);
  };

  const handleFlag = async (id) => {
    if (!window.confirm('Flag this rating for review?')) {
      return;
    }

    try {
      const response = await apiService.flagRating(id, 'Inappropriate content');

      if (response.success) {
        alert('Rating flagged successfully');
        fetchRatings();
      } else {
        alert(response.message || 'Failed to flag rating');
      }
    } catch (err) {
      alert('Failed to flag rating');
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const getRatingStars = (rating) => {
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'success';
    if (rating >= 3) return 'warning';
    return 'error';
  };

  const columns = [
    {
      field: 'crn',
      headerName: 'CRN',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
          {params.row.crn || params.row.rideId?.substring(0, 8) + '...'}
        </Typography>
      ),
    },
    {
      field: 'reviewerName',
      headerName: 'Rated By',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.reviewerName || params.row.raterName || '-'}
        </Typography>
      ),
    },
    {
      field: 'targetName',
      headerName: 'Driver Name',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {params.row.targetName || params.row.ratedName || '-'}
        </Typography>
      ),
    },
    {
      field: 'driverPhone',
      headerName: 'Driver Phone Number',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.row.driverPhone || params.row.targetPhone || params.row.mobile || '-'}
        </Typography>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.type || params.row.ratingType || '-'}
          size="small"
          color="primary"
        />
      ),
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Chip
            label={params.row.rating}
            color={getRatingColor(params.row.rating)}
            size="small"
            sx={{ mr: 1 }}
          />
          <Typography variant="caption">{getRatingStars(params.row.rating)}</Typography>
        </Box>
      ),
    },
    {
      field: 'review',
      headerName: 'Review',
      width: 300,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.review || params.row.comment || '-'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 160,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" color="error" onClick={() => handleFlag(params.row.id)}>
          <Flag fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Driver Ratings & Reviews
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage driver ratings from passengers
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error} - Showing demo data
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by CRN, driver name, or phone number..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                },
              }}
            />
            {searchTerm && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  label={`${filteredRatings.length} result${filteredRatings.length !== 1 ? 's' : ''} found`}
                  size="small"
                  color="info"
                />
                <Chip
                  label="Clear"
                  size="small"
                  onClick={() => setSearchTerm('')}
                  variant="outlined"
                />
              </Stack>
            )}
          </Box>

          <DataGrid
            rows={filteredRatings}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default DriverRatingPage;